import { get, post, put, patch, del, setAuth, setActiveProfile, clearAuth, getToken, BASE_URL } from "./ApiService";
import {
  buildStudioFormData,
  parseStudioFormData,
} from "../models/StudioFormData";
import { parseStudioForm } from "../models/StudioForm";
import { buildLoginWithEmailAndPassword } from "../models/LoginWithEmailAndPassword";
import { buildLoginWithToken } from "../models/LoginWithToken";
import { parseLoginResponse } from "../models/LoginResponse";
import { parseUserProfile } from "../models/UserProfile";

const STUDIO_FORM_PATH = "/studio-form";

/**
 * Save a new form to the cloud.
 * @param {Object} state - App state (fields, sections, formDetails, etc.)
 * @returns {Promise<Object>} Created StudioFormData with base model fields
 */
export const saveFormToCloud = async (state) => {
  await ensureAuth();
  const body = buildStudioFormData(state);
  const json = await post(STUDIO_FORM_PATH, body);
  return parseStudioFormData(json);
};

/**
 * Update an existing form in the cloud.
 * @param {string} id - Form UUID
 * @param {Object} state - App state (fields, sections, formDetails, etc.)
 * @returns {Promise<Object>} Updated StudioFormData with base model fields
 */
export const updateFormToCloud = async (id, state) => {
  await ensureAuth();
  const body = buildStudioFormData(state);
  const json = await put(`${STUDIO_FORM_PATH}/${id}`, body);
  return parseStudioFormData(json);
};

/**
 * Delete a form from the cloud.
 * @param {string} id - Form UUID
 * @returns {Promise<void>}
 */
export const deleteFormFromCloud = async (id) => {
  await ensureAuth();
  await del(`${STUDIO_FORM_PATH}/${id}`);
};

/**
 * Get all studio forms from the cloud.
 * @returns {Promise<Array>} Array of StudioForm objects with base model fields
 */
export const getFormsFromCloud = async () => {
  await ensureAuth();
  const json = await get(STUDIO_FORM_PATH);
  const items = Array.isArray(json) ? json : [];
  return items.map((item) => {
    const form = parseStudioForm(item);
    form.assignedTo = item.assignedTo || null;
    form.assignedToUserProfile = item.assignedToUserProfile || null;
    return form;
  });
};

/**
 * Get a single studio form from the cloud by ID with expanded StudioFormData.
 * @param {string} id - StudioForm UUID
 * @returns {Promise<Object>} StudioForm with base model fields + studioFormDatas array
 */
export const getFormFromCloudById = async (id) => {
  await ensureAuth();
  const json = await get(`${STUDIO_FORM_PATH}/${id}?expand=studioFormData,publishedByProfile`);
  const studioForm = parseStudioForm(json);
  studioForm.description = json.description || "";
  studioForm.studioFormDatas = json.studioFormDatas || [];
  return studioForm;
};

/**
 * Get a single studio form data record by ID.
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} id - StudioFormData UUID
 * @returns {Promise<Object>} StudioFormData with base model fields
 */
export const getFormDataFromCloudById = async (studioFormId, id) => {
  await ensureAuth();
  const json = await get(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${id}?expand=pdf`);
  return parseStudioFormData(json);
};

/**
 * Delete a studio form data record from the cloud.
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} id - StudioFormData UUID
 * @returns {Promise<void>}
 */
export const deleteFormDataFromCloud = async (studioFormId, id) => {
  await ensureAuth();
  await del(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${id}`);
};

/**
 * Patch a studio form's name, description, and version.
 * @param {string} id - StudioForm UUID
 * @param {Object} body - { name, description, version }
 * @returns {Promise<void>}
 */
export const patchStudioForm = async (id, body) => {
  await ensureAuth();
  await patch(`${STUDIO_FORM_PATH}/${id}`, body);
};

/**
 * Patch a studio form data record's name, description, and version.
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} id - StudioFormData UUID
 * @param {Object} body - { name, description, version }
 * @returns {Promise<void>}
 */
export const patchStudioFormData = async (studioFormId, id, body) => {
  await ensureAuth();
  await patch(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${id}`, body);
};

/**
 * Toggle the "ready to deploy to STG" flag on a studio form data revision.
 * Stored server-side on the revision's metadata.stgReady.
 *
 * The backend enforces the rules and returns an error when they aren't met:
 * must be the latest revision; turning ON requires a form publish to a bucket
 * other than Staging; turning OFF requires revision ownership or studio_super_admin.
 *
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} studioFormDataId - StudioFormData UUID
 * @param {boolean} stgReady - true to mark ready to deploy to STG, false to clear
 * @returns {Promise<void>}
 */
export const setStgReady = async (studioFormId, studioFormDataId, stgReady) => {
  await ensureAuth();
  await patch(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/stg-ready`, { stgReady });
};

const STUDIO_ROLES = ["studio_super_admin", "studio_admin", "studio_json", "studio_annotator"];

/**
 * Resolves the active profile from the who-am-i endpoint.
 * Finds the first profile with a studio role and stores it on ApiService.
 *
 * @returns {Promise<Object>} UserProfile with id, email, displayName, avatarUrl
 * @throws {Error} If no profile with a studio role is found
 */
const resolveProfile = async () => {
  const json = await get("/who-am-i");
  const profiles = json?.profiles || [];

  const defaultProfile = profiles.find((p) => p.default === true);

  if (!defaultProfile) {
    throw new Error("No default profile found for this account.");
  }

  const hasStudioRole = (defaultProfile.roles || []).some((r) => STUDIO_ROLES.includes(r));

  if (!hasStudioRole) {
    throw new Error("Default profile does not have an authorized studio role.");
  }

  const profile = parseUserProfile(defaultProfile);
  profile.roles = defaultProfile.roles || [];
  setActiveProfile(profile);
  return profile;
};

/**
 * Validates the current JWT and silently refreshes it if expired.
 * Called before every authenticated API call.
 *
 * @throws {Error} If session cannot be restored (triggers logout in caller)
 */
const ensureAuth = async () => {
  try {
    await get("/ping-auth");
    return;
  } catch {
    // JWT expired or invalid — try to refresh
  }

  const email = localStorage.getItem("formStudioEmail");
  const savedToken = localStorage.getItem("formStudioToken");

  if (!email || !savedToken) {
    clearAuth();
    throw new Error("Session expired. Please log in again.");
  }

  try {
    const body = buildLoginWithToken(email, savedToken);
    const json = await post("/login-token", body);
    const loginResponse = parseLoginResponse(json);
    setAuth(loginResponse.jwt, loginResponse.token, loginResponse.secret);
    localStorage.setItem("formStudioToken", loginResponse.token);
    await resolveProfile();
  } catch {
    clearAuth();
    throw new Error("Session expired. Please log in again.");
  }
};

/**
 * Log in with email and password, then resolve the studio profile.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} The authenticated UserProfile
 */
export const loginWithEmailAndPassword = async (email, password) => {
  const body = buildLoginWithEmailAndPassword(email, password);
  const json = await post("/login", body);
  const loginResponse = parseLoginResponse(json);

  setAuth(loginResponse.jwt, loginResponse.token, loginResponse.secret);

  return resolveProfile();
};

/**
 * Log in with email and token (session restore), then resolve the studio profile.
 *
 * @param {string} email
 * @param {string} token
 * @returns {Promise<Object>} The authenticated UserProfile
 */
export const loginWithToken = async (email, token) => {
  const body = buildLoginWithToken(email, token);
  const json = await post("/login-token", body);
  const loginResponse = parseLoginResponse(json);

  setAuth(loginResponse.jwt, loginResponse.token, loginResponse.secret);

  return resolveProfile();
};

/**
 * Get all profiles that can be assigned to a studio form.
 * @returns {Promise<Array>} Array of profile objects
 */
export const getAssignableProfiles = async () => {
  await ensureAuth();
  return get(`${STUDIO_FORM_PATH}/assignable-profiles`);
};

/**
 * Assign a studio form to a profile.
 * @param {string} studioFormId - StudioForm UUID
 * @param {string} profileId - Profile UUID to assign
 * @returns {Promise<void>}
 */
export const assignForm = async (studioFormId, profileId) => {
  await ensureAuth();
  return put(`${STUDIO_FORM_PATH}/${studioFormId}/assign/${profileId}`);
};

/**
 * Unassign a studio form.
 * @param {string} studioFormId - StudioForm UUID
 * @returns {Promise<void>}
 */
export const unassignForm = async (studioFormId) => {
  await ensureAuth();
  return put(`${STUDIO_FORM_PATH}/${studioFormId}/unassign`);
};

export const markAnnotationsComplete = async (studioFormId, studioFormDataId) => {
  await ensureAuth();
  return put(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/complete/annotations`);
};

export const unmarkAnnotationsComplete = async (studioFormId, studioFormDataId) => {
  await ensureAuth();
  return del(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/complete/annotations`);
};

export const markJsonComplete = async (studioFormId, studioFormDataId) => {
  await ensureAuth();
  return put(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/complete/json`);
};

export const unmarkJsonComplete = async (studioFormId, studioFormDataId) => {
  await ensureAuth();
  return del(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/complete/json`);
};

/**
 * Create a metrics tracking event.
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} studioFormDataId - StudioFormData UUID
 * @param {Object} event - { profileId, type, source?, dirtyType?, duration? }
 * @returns {Promise<Object>} Created event with id
 */
export const createMetricsEvent = async (studioFormId, studioFormDataId, event) => {
  await ensureAuth();
  return post(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/metrics`, event);
};

/**
 * Update a metrics tracking event (e.g. pause duration).
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} studioFormDataId - StudioFormData UUID
 * @param {string} eventId - Metrics event UUID
 * @param {Object} body - { profileId, duration }
 * @returns {Promise<Object>}
 */
export const updateMetricsEvent = async (studioFormId, studioFormDataId, eventId, body) => {
  await ensureAuth();
  return patch(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/metrics/${eventId}`, body);
};

/**
 * Send a close event via navigator.sendBeacon (fire-and-forget, no JWT).
 * Uses token-in-query-param auth on a separate endpoint.
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} studioFormDataId - StudioFormData UUID
 * @param {Object} body - { profileId, type: "close", duration? }
 */
export const sendMetricsBeacon = (studioFormId, studioFormDataId, body) => {
  const token = getToken();
  const url = `${BASE_URL}${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/close?token=${encodeURIComponent(token)}`;
  const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
  navigator.sendBeacon(url, blob);
};

/**
 * Publish a studio form data record.
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} studioFormDataId - StudioFormData UUID
 * @param {Object} body - { name, description, version, env, jsonType, json }
 * @returns {Promise<Object>}
 */
export const publishFormData = async (studioFormId, studioFormDataId, body) => {
  await ensureAuth();
  return post(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/publish`, body);
};

/**
 * Get tracking events for a single studio form data record.
 * @param {string} studioFormId - Parent StudioForm UUID
 * @param {string} studioFormDataId - StudioFormData UUID
 * @returns {Promise<Array>} Array of event objects
 */
export const getFormDataEvents = async (studioFormId, studioFormDataId) => {
  await ensureAuth();
  return get(`${STUDIO_FORM_PATH}/${studioFormId}/studio-form-data/${studioFormDataId}/events`);
};

/**
 * Get tracking events for all revisions of a studio form.
 * @param {string} studioFormId - StudioForm UUID
 * @returns {Promise<Array>} Array of { studioFormDataId, updatedAt, name, version, events[] }
 */
export const getFormEvents = async (studioFormId) => {
  await ensureAuth();
  return get(`${STUDIO_FORM_PATH}/${studioFormId}/events`);
};

export default {
  loginWithEmailAndPassword,
  loginWithToken,
  saveFormToCloud,
  updateFormToCloud,
  deleteFormFromCloud,
  getFormsFromCloud,
  getFormFromCloudById,
  getFormDataFromCloudById,
  deleteFormDataFromCloud,
  patchStudioForm,
  patchStudioFormData,
  setStgReady,
  getAssignableProfiles,
  assignForm,
  unassignForm,
  createMetricsEvent,
  updateMetricsEvent,
  sendMetricsBeacon,
  publishFormData,
  getFormDataEvents,
  getFormEvents,
};
